import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { FilterMatchMode } from 'primereact/api';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { useAuth } from '../contexts/AuthContext.js';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import '../config.js';
import { ScrollTop } from 'primereact/scrolltop';
import { ProgressBar } from 'primereact/progressbar';   
import { OverlayPanel } from 'primereact/overlaypanel';
import { Messages } from 'primereact/messages';
import useStatusPageScript from '../hooks/useStatusPageScript.js';
import Login from './Login.js';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Toolbar } from 'primereact/toolbar';
import { Tag } from 'primereact/tag';
import { InputTextarea } from 'primereact/inputtextarea';
import LocationSettingsDialog from './LocationSettingsDialog';
import { canAccessCustomerWalletBranding, canAccessLocationWalletPortal, canAccessTileTimeLimitation } from '../utils/accessControl';
import walletApi from '../api/walletApi';


const SupportLocations = () => {
  // Load the status page script
  useStatusPageScript();
  
  const { id } = useParams();
  const [events, setEvents] = useState([]);
  const [portalCodes, setPortalCodes] = useState({});
  const [savingPortalCodeId, setSavingPortalCodeId] = useState(null);
  const navigate = useNavigate();
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const { accessContext, logout } = useAuth();
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
  });
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState(20);
  const op = useRef(null);
  const msgs = useRef(null);
  const [title, setTitle] = useState('Sites');
  const [appid, setAppId] = useState(0);
  const [useAppId, setUseAppId] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const toast = useRef(null);
  const [showOptions, setShowOptions] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showLocationSettingsDialog, setShowLocationSettingsDialog] = useState(false);
  const [locationSettingsTarget, setLocationSettingsTarget] = useState(null);
  const toastBC = useRef(null);
  const editmsgs = useRef(null);
  const [checkHubStatus, setCheckHubStatus] = useState(false);
  const [lms, setLms] = useState('the LMS');
  const hasTileTimeLimitationAccess = canAccessTileTimeLimitation(accessContext);
  const isCustomerLevelUser = canAccessCustomerWalletBranding(accessContext);
  const isLocationLevelUser = canAccessLocationWalletPortal(accessContext);
  const customerId = accessContext && accessContext.claims && accessContext.claims.customerId !== null && accessContext.claims.customerId !== undefined
    ? Number(accessContext.claims.customerId)
    : Number(localStorage.getItem('id'));
  const appProviderId = accessContext && accessContext.claims && accessContext.claims.appProviderId !== null && accessContext.claims.appProviderId !== undefined
    ? accessContext.claims.appProviderId
    : localStorage.getItem('appid');

    const handleLoginSuccess = () => {
        setShowLoginModal(false);
        if (toast.current) {
            toast.current.show({severity: 'success', summary: '', detail: 'Login successful! You can continue working.'});
        }
        getEvents(false);
    };

  useEffect(() => {
    if (isLocationLevelUser) {
      const locationName = accessContext && accessContext.claims && accessContext.claims.locationName
        ? String(accessContext.claims.locationName)
        : '';

      if (locationName) {
        localStorage.setItem('supporttile', locationName);
        localStorage.setItem('supportsite', locationName);
        localStorage.setItem('tilesfrom', 'sites');
        navigate('/support/tiles', { replace: true });
        return;
      }
    }

    let appid = localStorage.getItem('appid');
    if(appid !== null){
      setAppId(appid);
      setUseAppId(true);
    }

    const fetchEvents = async () => {
        getEvents(false);
    };

    fetchEvents();
    let _filters = JSON.parse(localStorage.getItem('locationsFilters'));
    if (_filters) {
      setFilters(_filters);
      setGlobalFilterValue(_filters.global.value);
    }
    let customer = localStorage.getItem('supportcustomer');
    if(customer !== null){
      setTitle(customer + ' - Sites');
    }

  }, [accessContext, id, isLocationLevelUser, navigate]);

  const getEvents = async (checkHub) => {
    try {
        setLoading(true);
        setEvents([]);
        const token = localStorage.getItem('token');
        const id = localStorage.getItem('id');
        let url =  global.config.url + `/support/locations/${id}/${checkHub}`;
        let appid = localStorage.getItem('appid');
        if(appid !== null || useAppId){
          url = url + `?appProviderId=${appid}`;
        }
        const response = await axios.get(url, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          let urlLms = global.config.url + `/support/lms/${id}`;
          if(appid !== null || useAppId){
            urlLms = urlLms + `?appProviderId=${appid}`;
          }
          const lms = await axios.get(urlLms, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          if (lms.status === 200) {
            setLms(lms.data);
          }
          setEvents(response.data);
          setPortalCodes((response.data || []).reduce((codes, location) => ({ ...codes, [location.id]: location.walletPassPortalCode || location.walletPassesPortalCode || '' }), {}));
          setLoading(false);
      } catch (error) {
        console.error('Error fetching event record:', error);
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            setShowLoginModal(true);
          }
          setLoading(false);
          setEvents([]);
      }
      

    };

  const onPageChange = (event) => {
    setLoading(true);
    setRows(event.rows);
    getEvents(checkHubStatus);
  };
  
  const optionsSelected = (e, rowData) =>{
      // op.current.toggle(e);
      setRowSelectionModel(rowData);
    }

  const optionsTemplate = (rowData) => {
    return <Button link icon="pi pi-ellipsis-v" onClick={(e) => optionsSelected(e, rowData)} />;
  };

  const getAccessPoints = async () => {
    if(rowSelectionModel[0] === undefined || rowSelectionModel[0].id === 0 || rowSelectionModel[0].name === null){
      msgs.current.show({severity: 'error', summary: '', detail: 'Please select a location to view its tiles.'});
    }
    else{
      // navigate(`/accesspoints/client/${rowSelectionModel.name}/${rowSelectionModel.id}`);
      // msgs.current.show({severity: 'info', summary: '', detail: rowSelectionModel.name + ' access points are not yet implemented.'});
      localStorage.setItem('supporttile', rowSelectionModel[0].name);
      localStorage.setItem('tilesfrom', 'sites');
      localStorage.setItem('supportsite', rowSelectionModel[0].name);
      navigate(`/support/tiles/`);
    }
  }

  const getClients = async () => {
    if(rowSelectionModel[0] === undefined || rowSelectionModel[0].id === 0 || rowSelectionModel[0].name === null){
      msgs.current.show({severity: 'error', summary: '', detail: 'Please select a site to view its clients.'});
    }
    else{
      // navigate(`/accesspoints/client/${rowSelectionModel.name}/${rowSelectionModel.id}`);
      // msgs.current.show({severity: 'info', summary: '', detail: rowSelectionModel.name + ' access clients are not yet implemented.'});
      localStorage.setItem('supportsite', rowSelectionModel[0].name);
      navigate(`/support/clients/`);
    }
  }

  const openLocationSettings = (location) => {
    if (!hasTileTimeLimitationAccess) return;

    setLocationSettingsTarget(location);
    setRowSelectionModel([location]);
    setShowOptions(false);
    setShowLocationSettingsDialog(true);
  };

  const closeLocationSettings = () => {
    setShowLocationSettingsDialog(false);
    setLocationSettingsTarget(null);
  };

  const startContent = (
      <React.Fragment>
        <div>
          <Button onClick={() => getClients()} label="Get Clients" icon="pi pi-desktop" className="p-button-primary mb-3 ml-3" tooltip="Get clients for this location" tooltipOptions={{position:"bottom"}}/>
          <Button onClick={() => getAccessPoints()} label="Get Tiles" icon="pi pi-qrcode" className="p-button-warning mb-3 ml-3" tooltip="Get tiles for this location" tooltipOptions={{position:"bottom"}}/>
          <Button onClick={() => {setShowEditDialog(true); setShowOptions(false)}} label="Edit Location" icon="pi pi-pencil" className="p-button-info mb-3 ml-3" tooltip="Edit selected location" tooltipOptions={{position:"bottom"}}/>
          {hasTileTimeLimitationAccess && rowSelectionModel[0] && (
            <Button onClick={() => openLocationSettings(rowSelectionModel[0])} label="Location settings" icon="pi pi-cog" className="p-button-help mb-3 ml-3" tooltip="Manage tile time limitation for this location" tooltipOptions={{position:"bottom"}}/>
          )}
        </div>
      </React.Fragment>
  );

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };

    _filters['global'].value = value;

    localStorage.setItem('locationsFilters', JSON.stringify(_filters));

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const clearFilter = () => {
  let _filters = { ...filters };
  _filters['global'].value = null;
  setFilters(_filters);
  setGlobalFilterValue('');
  localStorage.removeItem('locationsFilters');
};

const updateLocation = async (location) => {
    // Implement location update logic here
    try{
        setLoading(true);
        toastBC.current.clear();
        const token = localStorage.getItem('token');
        const id = localStorage.getItem('id');
        let url =  global.config.url + `/support/locations/${location.id}/${id}`;
        let appid = localStorage.getItem('appid');
        if(appid !== null || useAppId){
          url = url + `?appProviderId=${appid}`;
        }
        const response = await axios.put(url, location, {
            headers: {
              Authorization: `Bearer ${token}`,  
            },
          });
          setLoading(false);
          if(response.status === 200){
            msgs.current.show({severity: 'success', summary: '', detail: 'Location updated successfully.'});
            setShowEditDialog(false);
            getEvents(false);
          }
    } catch (error) {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            setShowLoginModal(true);
          }
        else{

          if(error.response && error.response.data)
          {
            editmsgs.current.show({severity: 'error', summary: '', detail: error.response.data || 'Error updating location.'});
          }
          else{
            editmsgs.current.show({severity: 'error', summary: '', detail: 'Error updating location.'});
          }
        }
        setLoading(false);
    }
        
  };
  const clear = () => {
  toastBC.current.clear();
};

  const confirmEdit = (newdata) => {
      toastBC.current.show({
        severity: 'info',
        sticky: true,
        className: 'border-none',
        content: (
            <div className="flex flex-column align-items-center" style={{ flex: '1' }}>
                <div className="text-center">
                    <i className="pi pi-exclamation-triangle" style={{ fontSize: '3rem' }}></i>
                    <div className="font-bold text-xl my-3">Are you sure?</div>
                </div>
                <div className="flex align-items-center">
                    <Button onClick={(e) => updateLocation(newdata)} type="button" label="Confirm" className="p-button-success w-6rem" />
                    <Button onClick={(e) => clear(false)} type="button" label="Cancel" className="p-button-warning w-6rem mx-4" />
                </div>
            </div>
        )
      });
  };

const onRowEditComplete = (e) => {
    let _events = [...events];
    let { newData, index } = e;
    _events[index] = newData;
    setEvents(_events);
    confirmEdit(newData);
  };

const goBack = () => {
      localStorage.removeItem('locationsFilters');
      navigate(-1);
    }

  const progressbar = (        
    <div className="card mt-3">
        <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
    </div>);

  const logoutUser = async () => {
    await logout();
    navigate('/login');
  }

  const checkHubStatusChanged = (e) => {
    if(e.value){
      msgs.current.show({severity: 'info', summary: '', detail: 'Reloading locations and checking Hubs status, this may take a while...', life: 15000, closeable: true});
    }
    else{
      msgs.current.show({severity: 'info', summary: '', detail: 'Reloading locations without checking Hubs status...'});
    }
    setCheckHubStatus(e.value);
    getEvents(e.value);
  }


  const getSeverity = (location) => {
      let hubsStatus = location.hubsStatus || '';
      // if hubStatus contains the work "Offline", return 'danger'
      if (hubsStatus.toLowerCase().includes('offline') && !hubsStatus.toLowerCase().includes('online')) {
          return 'danger';
      }
      else if (hubsStatus.toLowerCase().includes('online') && !hubsStatus.toLowerCase().includes('offline')) {
          return 'success';
      }
      else if (hubsStatus.toLowerCase().includes('offline') && hubsStatus.toLowerCase().includes('online')) {
          return 'warning';
      }
      else {
          return 'info';
      }
    };
    const statusBodyTemplate = (location) => {
        return <Tag value={location.hubsStatus.toString()} severity={getSeverity(location)}></Tag>;
    };

  const locationSettingsBodyTemplate = (location) => (
    <Button
      label="Location settings"
      icon="pi pi-cog"
      className="p-button-text"
      onClick={() => openLocationSettings(location)}
    />
  );

  const hasWalletPassLocations = isCustomerLevelUser && events.some((location) => Boolean(location.walletPassesEnabled));

  const walletPortalBodyTemplate = (location) => {
    if (!location.walletPassesEnabled) return null;
    const code = portalCodes[location.id] || '';
    return (
      <div className="flex align-items-center flex-wrap gap-2">
        <Tag value="Enabled" severity="success" />
        <InputText value={code} onChange={(e) => setPortalCodes((current) => ({ ...current, [location.id]: e.target.value }))} aria-label={`Wallet portal code for ${location.name}`} style={{ width: '10rem' }} />
        <Button icon="pi pi-save" className="p-button-text" loading={savingPortalCodeId === location.id} onClick={async () => {
          const nextCode = String(portalCodes[location.id] || '').trim();
          if (!nextCode) { msgs.current.show({ severity: 'error', summary: '', detail: 'Enter a Wallet Pass Portal code.' }); return; }
          try { setSavingPortalCodeId(location.id); await walletApi.saveLocationPortalCode(location.id, nextCode); msgs.current.show({ severity: 'success', summary: '', detail: `Wallet Pass Portal code updated for ${location.name}.` }); }
          catch (error) { msgs.current.show({ severity: 'error', summary: '', detail: (error.response && error.response.data) || 'Failed to update Wallet Pass Portal code.' }); }
          finally { setSavingPortalCodeId(null); }
        }} tooltip="Save portal code" />
        <Button icon="pi pi-external-link" className="p-button-text" onClick={() => window.open('/wallet-pass-portal', '_blank', 'noopener,noreferrer')} tooltip="Open Wallet Pass Portal" />
      </div>
    );
  };

  return (
    <Card className='justify-content-md-center mt-5'>
      <Container fluid="md" >
      <div className="panel">
        <div className="flex align-content-center flex-wrap">
          <Button link icon="pi pi-arrow-left" onClick={() => goBack()} className='flex mb-3' visible={useAppId && !isCustomerLevelUser} />
          <h4 className='flex mt-2 mb-3'>{title}</h4> 
          <span className="p-input-icon-left mb-3 ml-3">
            <i className="pi pi-search" />
            <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Filter Search" tooltip="Start typing to filter" 
              tooltipOptions={{position: "bottom"}}/>
          </span>
          <Button icon="pi pi-times" className="p-button-text mb-3"  onClick={() => clearFilter()} tooltip="Clear filter" tooltipOptions={{position: "bottom"}} />
          <Button label="Check Hubs Status" visible={!checkHubStatus} className='p-button-info ml-3 mb-3' onClick={() => checkHubStatusChanged({value: true})} tooltip="Reload locations and check Hub status" tooltipOptions={{position:"bottom"}}/>
          <Button label="Uncheck Hubs Status" visible={checkHubStatus} className='p-button-warning ml-3 mb-3' onClick={() => checkHubStatusChanged({value: false})} tooltip="Reload locations without checking Hub status" tooltipOptions={{position:"bottom"}}/>
        </div>
      </div>
      {loading && progressbar}
        <Messages ref={msgs} />
        <Toast ref={toast} />
        <Toast ref={toastBC} position="top-center" />
        <Row className='justify-content-md-center mt-5'>
            <Col>
            <Dialog visible={showOptions} onHide={() => setShowOptions(false)} style={{ minWidth: '5rem' }}>
              {startContent}
            </Dialog>
            <DataTable value={events} selectionMode={'radiobutton'} selection={rowSelectionModel} onSelectionChange={(e) => setRowSelectionModel(e.value)} 
            onRowSelect={() => setShowOptions(true)} editMode='row' onRowEditComplete={onRowEditComplete}
            dataKey="id" tableStyle={{ minWidth: '50rem' }} filters={filters} filterDisplay="menu" emptyMessage={loading ? "Loading locations..." : "No locations found."}
            globalFilterFields={['name', 'extra']}  
            scrollable scrollHeight="60vh">
                {/* <Column field="options" header="" body={optionsTemplate}> </Column> */}
                <Column field="name" header="Name" showFilterMenu={false} filterMenuStyle={{ width: '14rem' }} style={{ minWidth: '12rem' }} sortable></Column>
                <Column field="extra" header="Site/Venue ID"  sortable></Column>
                <Column field="note" header="Notes" style={{ minWidth: '12rem' }} sortable></Column>
                <Column field="hubAndRelay" header="Hub and Relay" style={{ minWidth: '12rem' }} sortable></Column>
                <Column  header="Hubs Status" style={{ minWidth: '12rem' }} body={statusBodyTemplate} ></Column>
                {lms.key === "Gladstone" && (<Column field="controller" header="Controller" style={{ minWidth: '12rem' }} sortable></Column>)}
                {hasTileTimeLimitationAccess && (
                  <Column header="Location settings" body={locationSettingsBodyTemplate} style={{ minWidth: '14rem' }}></Column>
                )}
                {hasWalletPassLocations && (
                  <Column header="Wallet Pass Portal" body={walletPortalBodyTemplate} style={{ minWidth: '25rem' }}></Column>
                )}
                
                {/* <Column rowEditor headerStyle={{ width: '5%', minWidth: '1rem' }} bodyStyle={{ textAlign: 'center' }}></Column> */}
            </DataTable>
            </Col>

        </Row>
        {rowSelectionModel[0] !== undefined &&  (
          <div className="card flex justify-content-center mt-3 mb-3">
            <Dialog className='' visible={showEditDialog} onHide={() => setShowEditDialog(false)} footer={
              <div className='flex justify-content-center mb-3'>
                <Button label="Cancel" icon="pi pi-times" className="p-button-warning" onClick={() => setShowEditDialog(false)} tooltip="Cancel changes" tooltipOptions={{position:"bottom"}}/>
                <Button label="Save" icon="pi pi-check" className="p-button-success ml-3" onClick={() => {onRowEditComplete({newData: rowSelectionModel[0], index: events.findIndex(x => x.name === rowSelectionModel[0].name)});}} tooltip="Save changes" tooltipOptions={{position:"bottom"}}/>
              </div>
            } >
            <Messages ref={editmsgs} />
            <div className="card">
              <Toolbar className='' start={<h4>Edit Location {rowSelectionModel[0].name}</h4>}/>
            </div>
              <div className='justify-content-md-center mb-3 mt-5'>
                <label className='mr-3'>Site/Venue ID:</label>
                <InputText className='mr-3' value={rowSelectionModel[0].extra} onChange={(e) => setRowSelectionModel([{...rowSelectionModel[0], extra: e.target.value}])} placeholder="Site/Venue ID"/>
              </div>
              <div className='justify-content-md-center'>
                <label className='mr-3'>Notes:</label>
              </div>
              <div className='justify-content-md-center'>
                <InputTextarea className='mt-3' value={rowSelectionModel[0].note} onChange={(e) => setRowSelectionModel([{...rowSelectionModel[0], note: e.target.value}])} placeholder="Add location notes" rows={4} cols={50} autoResize />
              </div>
            </Dialog>
          </div>
        )}
        <OverlayPanel ref={op}>
          {startContent}
        </OverlayPanel>
        <LocationSettingsDialog
          visible={showLocationSettingsDialog}
          onHide={closeLocationSettings}
          location={locationSettingsTarget}
          customerId={customerId}
          appProviderId={appProviderId}
          onSaved={() => {
            closeLocationSettings();
          }}
          onUnauthorized={() => {
            closeLocationSettings();
            setShowLoginModal(true);
          }}
        />
        <div className="card flex justify-content-center">
          <Dialog 
            header="Session Expired - Please Login Again" 
            visible={showLoginModal} 
            style={{ width: '450px' }} 
            modal={true}
            closable={false}
            className="justify-content-center"
          >
            <p className="mb-3">Your session has expired. Please log in again to continue working without losing your form data.</p>
            <div className="flex justify-content-center">
              <Login onLoginSuccess={handleLoginSuccess} />
            </div>
          </Dialog>
        </div>
      </Container>
    <ScrollTop />
    </Card>
  );
};

export default SupportLocations;
